import type { QualitySample } from "../api/parse.ts";

export type NetworkGrade = "excellent" | "good" | "poor" | "unstable";

export type QualityPhase = NetworkGrade | "measuring" | "no-data" | "no-media";

export type NetworkGradeBand = {
  rttMsBelow: number;
  jitterMsBelow: number;
  lossBelow: number;
};

export type NetworkGradeThresholds = {
  excellent: NetworkGradeBand;
  good: NetworkGradeBand;
  poor: NetworkGradeBand;
};

export const NETWORK_GRADE_THRESHOLDS: NetworkGradeThresholds = {
  excellent: { rttMsBelow: 80, jitterMsBelow: 15, lossBelow: 0.005 },
  good: { rttMsBelow: 150, jitterMsBelow: 30, lossBelow: 0.02 },
  poor: { rttMsBelow: 300, jitterMsBelow: 50, lossBelow: 0.05 },
};

const STALL_FPS_BELOW = 1;
const SLOW_DEVICE_MS = 40;

export const MIN_SAMPLES = 3;
export const FEED_SILENT_MS = 30_000;
export const MIN_HOLD_MS = 4_000;
export const BAD_WINDOWS_FOR_UNSTABLE = 2;
export const GOOD_WINDOWS_TO_LEAVE_UNSTABLE = 2;
export const DEGRADE_DWELL_MS = 6_000;
export const IMPROVE_DWELL_MS = 4_000;
export const NO_MEDIA_MS = 30_000;

const GRADE_LABEL: Record<QualityPhase, string> = {
  excellent: "Excellent",
  good: "Good",
  poor: "Poor",
  unstable: "Unstable",
  measuring: "Measuring",
  "no-data": "No data",
  "no-media": "No media",
};

const RANK: Record<NetworkGrade, number> = { excellent: 0, good: 1, poor: 2, unstable: 3 };

export type GradeSignal = "rtt" | "jitter" | "loss" | "stall" | "device";

export type GradeWorst = {
  signal: GradeSignal;
  value: number;
  limit: number;
};

export type NetworkGradeReading = {
  grade: QualityPhase;
  label: string;
  tooltip: string;
  why: string | null;
  worst: GradeWorst | null;
};

type LinkSignal = "rtt" | "jitter" | "loss";

type Assessment = {
  grade: NetworkGrade | "unmeasured";
  worst: GradeWorst | null;
  cause: "link" | "stall" | "none";
};

export type GradeState = {
  phase: QualityPhase;
  lastStamp: number;
  changedAt: number;
  seenMedia: boolean;
  seenMotion: boolean;
  shown: boolean;
  usable: number;
  badRun: number;
  goodRun: number;
  badSince: number | null;
  goodSince: number | null;
  armedAt: number | null;
  clearAt: number | null;
  sample: QualitySample | null;
  assessment: Assessment | null;
};

export function initialGradeState(): GradeState {
  return {
    phase: "measuring",
    lastStamp: Number.NaN,
    changedAt: Number.NaN,
    seenMedia: false,
    seenMotion: false,
    shown: false,
    usable: 0,
    badRun: 0,
    goodRun: 0,
    badSince: null,
    goodSince: null,
    armedAt: null,
    clearAt: null,
    sample: null,
    assessment: null,
  };
}

export function advanceGrade(
  state: GradeState,
  samples: readonly QualitySample[],
  thresholds: NetworkGradeThresholds = NETWORK_GRADE_THRESHOLDS,
): GradeState {
  let next = state;
  for (const sample of samples) {
    const parsed = Date.parse(sample.sampledAt);
    if (Number.isFinite(next.lastStamp) && Number.isFinite(parsed) && parsed <= next.lastStamp) continue;
    next = gradeStep(next, sample, thresholds);
  }
  return next;
}

export function gradeHeld(
  state: GradeState,
  samples: readonly QualitySample[],
  arrivals: readonly number[],
  nowMs: number,
  thresholds: NetworkGradeThresholds = NETWORK_GRADE_THRESHOLDS,
): { state: GradeState; reading: NetworkGradeReading } {
  const next = advanceGrade(state, samples, thresholds);
  return { state: next, reading: finish(next, arrivals, nowMs) };
}

export function gradeFeed(
  samples: readonly QualitySample[] | null,
  arrivals: readonly number[],
  nowMs: number,
  thresholds: NetworkGradeThresholds = NETWORK_GRADE_THRESHOLDS,
): NetworkGradeReading {
  return gradeHeld(initialGradeState(), samples ?? [], arrivals, nowMs, thresholds).reading;
}

function gradeStep(state: GradeState, sample: QualitySample, thresholds: NetworkGradeThresholds): GradeState {
  const parsed = Date.parse(sample.sampledAt);
  const stamp = Number.isNaN(parsed) ? state.lastStamp : parsed;
  if (sample.fps !== 0 || sample.bitrate !== 0) state = { ...state, seenMotion: true };
  if (!state.seenMedia && stalled(sample)) return { ...state, lastStamp: stamp };
  state = { ...state, seenMedia: true };
  const assessment = applyDeviceCap(
    stalled(sample) ? stallAssessment(sample) : assessLink(sample, thresholds),
    sample,
  );
  const missing = assessment.cause === "none";
  if (missing) return state.shown ? { ...state, lastStamp: stamp } : { ...state, lastStamp: stamp, sample };
  const bad = assessment.cause === "stall" || assessment.grade === "unstable";
  const badRun = bad ? state.badRun + 1 : 0;
  const goodRun = bad ? 0 : state.goodRun + 1;
  const badSince = bad ? (state.badSince ?? stamp) : null;
  const goodSince = bad ? null : (state.goodSince ?? stamp);
  const armedAt = bad && badRun >= BAD_WINDOWS_FOR_UNSTABLE ? (state.armedAt ?? stamp) : null;
  const clearAt = !bad && goodRun >= GOOD_WINDOWS_TO_LEAVE_UNSTABLE ? (state.clearAt ?? stamp) : null;
  const next: GradeState = {
    ...state,
    lastStamp: stamp,
    usable: state.usable + 1,
    badRun,
    goodRun,
    badSince,
    goodSince,
    armedAt,
    clearAt,
  };
  if (next.usable < MIN_SAMPLES) return { ...next, phase: "measuring" };
  const unstableReady = bad && armedAt !== null && stamp - armedAt >= DEGRADE_DWELL_MS;
  const recovered = !bad && clearAt !== null && stamp - clearAt >= IMPROVE_DWELL_MS;
  let desired: QualityPhase = next.phase;
  if (next.phase === "unstable") desired = recovered && assessment.grade !== "unmeasured" ? assessment.grade : "unstable";
  else if (unstableReady) desired = "unstable";
  else if (!bad && assessment.grade !== "unmeasured") desired = assessment.grade;
  if (desired === next.phase) return assessment.grade === next.phase ? { ...next, sample, assessment } : next;
  const holdOk = next.phase === "measuring" || stamp - next.changedAt >= MIN_HOLD_MS;
  if (!holdOk) return next;
  return { ...next, shown: true, phase: desired, changedAt: stamp, sample, assessment };
}

function overlay(phase: QualityPhase, arrivals: readonly number[], nowMs: number): QualityPhase {
  if (arrivals.length === 0) return phase;
  let shown: QualityPhase = phase;
  let entered: number | null = null;
  for (let index = 1; index < arrivals.length; index += 1) {
    const previous = arrivals[index - 1];
    const arrival = arrivals[index];
    if (arrival - previous > FEED_SILENT_MS) {
      entered = previous + FEED_SILENT_MS;
      shown = "no-data";
    }
    if (shown === "no-data" && entered !== null && arrival - entered >= MIN_HOLD_MS) {
      shown = phase;
      entered = null;
    }
  }
  const last = arrivals[arrivals.length - 1];
  if (nowMs - last > FEED_SILENT_MS) return "no-data";
  if (shown === "no-data" && entered !== null && nowMs - entered >= MIN_HOLD_MS) return phase;
  return shown;
}

function finish(state: GradeState, arrivals: readonly number[], nowMs: number): NetworkGradeReading {
  const phase = overlay(state.phase, arrivals, nowMs);
  if (phase === "no-data") return present(phase, state.sample, state.assessment);
  if (phase === "measuring" && !state.seenMotion && mediaWaitExpired(arrivals, nowMs)) {
    return {
      grade: "no-media",
      label: GRADE_LABEL["no-media"],
      tooltip: "No media. Frames have not started.",
      why: null,
      worst: null,
    };
  }
  return present(phase, state.sample, state.assessment);
}

function mediaWaitExpired(arrivals: readonly number[], nowMs: number): boolean {
  const first = arrivals[0];
  const last = arrivals[arrivals.length - 1];
  if (first === undefined || last === undefined) return false;
  if (nowMs - last > FEED_SILENT_MS) return false;
  return nowMs - first >= NO_MEDIA_MS;
}

function assessLink(sample: QualitySample, thresholds: NetworkGradeThresholds): Assessment {
  if (sample.rttMs === null && sample.jitterMs === null && sample.packetLoss === null) {
    return { grade: "unmeasured", worst: null, cause: "none" };
  }
  const candidates: Assessment[] = [];
  if (sample.rttMs !== null) candidates.push(linkAssessment("rtt", sample.rttMs, thresholds));
  if (sample.jitterMs !== null) candidates.push(linkAssessment("jitter", sample.jitterMs, thresholds));
  if (sample.packetLoss !== null) candidates.push(linkAssessment("loss", sample.packetLoss, thresholds));
  const first = candidates[0];
  if (!first) return { grade: "unmeasured", worst: null, cause: "none" };
  return candidates.reduce((current, next) => (outranks(next, current) ? next : current));
}

function outranks(next: Assessment, current: Assessment): boolean {
  const rankDiff = rankOf(next.grade) - rankOf(current.grade);
  if (rankDiff !== 0) return rankDiff > 0;
  return pressure(next.worst) > pressure(current.worst);
}

function pressure(worst: GradeWorst | null): number {
  if (!worst) return 0;
  if (worst.limit === 0) return worst.value;
  return worst.value / worst.limit;
}

function linkAssessment(signal: LinkSignal, value: number, thresholds: NetworkGradeThresholds): Assessment {
  const key = signal === "rtt" ? "rttMsBelow" : signal === "jitter" ? "jitterMsBelow" : "lossBelow";
  const grade = lowerIsBetter(value, thresholds, key);
  if (grade === "excellent") return { grade, worst: null, cause: "link" };
  const limit =
    grade === "good" ? thresholds.excellent[key] : grade === "poor" ? thresholds.good[key] : thresholds.poor[key];
  return { grade, worst: { signal, value, limit }, cause: "link" };
}

function lowerIsBetter(
  value: number,
  thresholds: NetworkGradeThresholds,
  key: "rttMsBelow" | "jitterMsBelow" | "lossBelow",
): NetworkGrade {
  if (value < thresholds.excellent[key]) return "excellent";
  if (value < thresholds.good[key]) return "good";
  if (value < thresholds.poor[key]) return "poor";
  return "unstable";
}

function applyDeviceCap(held: Assessment, latest: QualitySample): Assessment {
  if (held.grade !== "excellent") return held;
  const capture = latest.captureMs >= SLOW_DEVICE_MS;
  const encode = latest.encodeMs >= SLOW_DEVICE_MS;
  if (!capture && !encode) return held;
  const useEncode = encode && (!capture || latest.encodeMs >= latest.captureMs);
  return {
    grade: "good",
    cause: "link",
    worst: {
      signal: "device",
      value: useEncode ? latest.encodeMs : latest.captureMs,
      limit: SLOW_DEVICE_MS,
    },
  };
}

function present(phase: QualityPhase, sample: QualitySample | null, assessment: Assessment | null): NetworkGradeReading {
  if (phase === "no-data") {
    return {
      grade: "no-data",
      label: GRADE_LABEL["no-data"],
      tooltip: "No data. The quality feed is quiet.",
      why: null,
      worst: null,
    };
  }
  if (phase === "no-media") {
    return {
      grade: "no-media",
      label: GRADE_LABEL["no-media"],
      tooltip: "No media. Frames have not started.",
      why: null,
      worst: null,
    };
  }
  if (phase === "measuring" || !sample || !assessment || assessment.grade === "unmeasured") {
    if (!sample) {
      return {
        grade: "measuring",
        label: GRADE_LABEL.measuring,
        tooltip: "Measuring session quality.",
        why: null,
        worst: null,
      };
    }
    return {
      grade: "measuring",
      label: GRADE_LABEL.measuring,
      tooltip: `Measuring, ${formatFps(sample.fps)} fps, ${formatKbit(sample.bitrate)} kbit/s.`,
      why: null,
      worst: null,
    };
  }
  const why = whyLine(phase, assessment.worst);
  const details = [
    sample.rttMs === null ? "round trip not measured" : `${Math.round(sample.rttMs)} ms round trip`,
    sample.jitterMs === null ? "jitter not measured" : `${Math.round(sample.jitterMs)} ms jitter`,
    sample.packetLoss === null ? "loss not measured" : `${percent(sample.packetLoss)} loss`,
    `${formatKbit(sample.bitrate)} kbit/s`,
  ];
  const head = why ?? GRADE_LABEL[phase];
  return {
    grade: phase,
    label: GRADE_LABEL[phase],
    tooltip: `${head}. ${details.join(", ")}.`,
    why,
    worst: phase === "excellent" ? null : assessment.worst,
  };
}

function whyLine(grade: NetworkGrade, worst: GradeWorst | null): string | null {
  if (!worst || grade === "excellent") return null;
  return `${GRADE_LABEL[grade]} because ${worstPhrase(worst)}`;
}

function worstPhrase(worst: GradeWorst): string {
  if (worst.signal === "rtt") return `round trip ${Math.round(worst.value)} ms (limit ${worst.limit} ms)`;
  if (worst.signal === "jitter") return `jitter ${Math.round(worst.value)} ms (limit ${worst.limit} ms)`;
  if (worst.signal === "loss") return `loss ${percent(worst.value)} (limit ${percent(worst.limit)})`;
  if (worst.signal === "device") return `the device is slow, ${Math.round(worst.value)} ms (limit ${worst.limit} ms)`;
  if (worst.value > 0) return `frame rate ${formatFps(worst.value)} fps (limit ${worst.limit} fps)`;
  return `bitrate 0 kbit/s (limit ${worst.limit} kbit/s)`;
}

export function qualityLogLine(reading: NetworkGradeReading): string {
  if (!reading.worst) return `wrc-quality label=${reading.grade} worst=none`;
  return `wrc-quality label=${reading.grade} worst=${reading.worst.signal}:${logNumber(reading.worst.value)}:${logNumber(reading.worst.limit)}`;
}

function stallAssessment(sample: QualitySample): Assessment {
  return { grade: "unstable", worst: stallWorst(sample), cause: "stall" };
}

function stalled(sample: QualitySample): boolean {
  return sample.fps < STALL_FPS_BELOW || sample.bitrate <= 0;
}

function stallWorst(sample: QualitySample): GradeWorst {
  if (sample.fps < STALL_FPS_BELOW) return { signal: "stall", value: sample.fps, limit: STALL_FPS_BELOW };
  return { signal: "stall", value: 0, limit: 1 };
}

function rankOf(grade: NetworkGrade | "unmeasured"): number {
  if (grade === "unmeasured") return -1;
  return RANK[grade];
}

function formatFps(fps: number): string {
  const rounded = Math.round(fps * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function formatKbit(bitrate: number): string {
  return String(Math.round(bitrate / 1000));
}

function logNumber(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return String(Math.round(value * 1000) / 1000);
}

function percent(loss: number): string {
  const rounded = Math.round(loss * 1000) / 10;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
}
